import { Link } from 'react-router-dom';
import { fetchInsightsVisitors } from '../../adminApi';
import { Panel } from '../../ui/Panel';
import { ADMIN_BASE } from '../../../utils/hosts';
import { dayShort, daysBetween, fromIso, weekNumber } from '../../../core/format/dates';
import { sum } from '../../../core/rules/insights/stats';
import { ChartFrame } from '../charts/ChartFrame';
import { BarChart } from '../charts/BarChart';
import { BarList } from '../charts/BarList';
import { Kpi } from '../Kpi';
import { ReportView } from '../ReportView';
import { useReport } from '../useReport';
import { fmt, ratio } from '../insightFormat';
import { deviceLabel } from '../insightLabels';
import type { PeriodState } from '../../sales/usePeriodParams';

/** Bezoekers (Umami): hoeveel, waarvandaan, welke pagina's en wat ze zoeken. */
export function VisitorsBlock({
  period
}: {
  period: PeriodState;
}) {
  const report = useReport(fetchInsightsVisitors, period.from, period.to);
  const span = `${period.from}-${period.to}`;
  return <ReportView report={report} what="De bezoekerscijfers" integration="umami" data-id="element-1954" data-mp="3dxadawd5iy2j">
      {v => {
      // De server geeft per dag, of per week bij een lange periode.
      const weekly = v.series.length > 1 && daysBetween(v.series[0].date, v.series[1].date) > 1;
      const total = sum(v.sources.map(s => s.visits));
      const deviceTotal = sum(v.devices.map(d => d.visits));
      const zero = v.searches.filter(s => s.results === 0);
      return <>
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2.2fr)]" data-id="element-1955" data-mp="2q7g3yos1jrn1">
              <section aria-label="Bezoek" className="rounded-lg border border-line bg-white p-5 sm:p-6" data-id="element-1956" data-mp="2l83515nwau43">
                <dl className="grid grid-cols-2 gap-6 lg:grid-cols-1" data-id="element-1957" data-mp="2h6htpor0enw0">
                  <Kpi size="lg" label="Bezoeken" value={v.visits} data-id="element-1958" data-mp="2h4ex2lvcpwib" />
                  <Kpi label="Bekeken pagina’s" value={v.pageviews} data-id="element-1959" data-mp="2c63n8b523m03" />
                </dl>
                <p className="mt-5 text-[12px] leading-relaxed text-muted" data-id="element-1960" data-mp="279d75nt94qeo">Zonder cookies geteld. Een bezoeker wordt per dag herkend, dus iemand die drie dagen komt, telt drie keer.</p>
              </section>
              <ChartFrame title="Bezoek" description={weekly ? 'Per week' : 'Per dag'} fileName={`bezoek-${span}`} table={{
            columns: [{
              label: weekly ? 'Week van' : 'Dag'
            }, {
              label: 'Bezoeken'
            }],
            rows: v.series.map(s => [dayShort(s.date), s.visits])
          }} data-id="element-1961" data-mp="22kkjyh3obe7z">
                <BarChart bars={v.series.map(s => ({
              key: s.date,
              label: weekly ? `wk ${weekNumber(fromIso(s.date))}` : String(Number(s.date.slice(8))),
              values: [s.visits]
            }))} series={[{
              label: 'Bezoeken',
              className: 'bg-clay/75'
            }]} format={n => fmt(n)} data-id="element-1962" data-mp="2gdjxwent50df" />
              </ChartFrame>
            </div>

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-id="element-1963" data-mp="2z8ijvzemm7m5">
              <ChartFrame title="Waar ze vandaan komen" fileName={`bronnen-bezoek-${span}`} table={{
            columns: [{
              label: 'Bron'
            }, {
              label: 'Bezoeken'
            }],
            rows: v.sources.map(s => [s.label, s.visits])
          }} data-id="element-1964" data-mp="341tr41ncoqel">
                <BarList rows={[...v.sources].sort((a, b) => b.visits - a.visits).map(s => ({
              key: s.source,
              label: s.label,
              value: s.visits,
              display: fmt(s.visits),
              meta: ratio(s.visits, total)
            }))} data-id="element-1965" data-mp="12bcsjcfe4inr" />
              </ChartFrame>
              <ChartFrame title="Meest bekeken" fileName={`paginas-${span}`} table={{
            columns: [{
              label: 'Pagina'
            }, {
              label: 'Bezoeken'
            }],
            rows: v.pages.map(p => [p.path, p.visits])
          }} data-id="element-1966" data-mp="9o05l15bi8tx">
                <BarList tone="ink" rows={v.pages.map(p => ({
              key: p.path,
              label: p.path,
              value: p.visits,
              display: fmt(p.visits)
            }))} data-id="element-1967" data-mp="ilaiss1p481h" />
              </ChartFrame>
              <ChartFrame title="Apparaat" fileName={`apparaten-${span}`} table={{
            columns: [{
              label: 'Apparaat'
            }, {
              label: 'Bezoeken'
            }],
            rows: v.devices.map(d => [deviceLabel[d.device], d.visits])
          }} data-id="element-1968" data-mp="1u17fx3joty9s">
                <BarList tone="sage" rows={v.devices.map(d => ({
              key: d.device,
              label: deviceLabel[d.device],
              value: d.visits,
              display: ratio(d.visits, deviceTotal)
            }))} data-id="element-1969" data-mp="2pc71o0p2k3q4" />
              </ChartFrame>
            </div>

            <Panel title="Zoeken op de site" description="Wat bezoekers in de webshop intikken. Zonder namen, e-mailadressen of nummers." data-id="element-1970" data-mp="2zbxwy4lgemr9">
              <div className="grid gap-8 md:grid-cols-2" data-id="element-1971" data-mp="348eu1oamsbgh">
                <div data-id="element-1972" data-mp="38ai7gigv1q4e">
                  <h3 className="mb-3 text-[13px] text-muted" data-id="element-1973" data-mp="38a00orwknti8">Meest gezocht</h3>
                  <BarList tone="ink" rows={v.searches.slice(0, 8).map(s => ({
                key: s.term,
                label: s.term,
                value: s.count,
                display: `${s.count}×`,
                meta: s.results === 0 ? 'niets gevonden' : undefined
              }))} data-id="element-1974" data-mp="3d3un433kaml0" />
                </div>
                <div data-id="element-1975" data-mp="3myw94q2jj8jd">
                  <h3 className="mb-3 text-[13px] text-muted" data-id="element-1976" data-mp="3rzk9418qyzpq">Zonder resultaat</h3>
                  {zero.length === 0 ? <p className="text-[13px] text-muted" data-id="element-1977" data-mp="1gdi3gzgv31ia">Alles wat gezocht werd, leverde iets op.</p> : <>
                      <ul className="divide-y divide-line text-[13px]" data-id="element-1978" data-mp="1bfqdiazs1pgq">
                        {zero.map(s => <li key={s.term} className="flex items-baseline justify-between gap-3 py-2" data-id="element-1979" data-mp="11ffxkimcy3xv">
                            <span className="text-ink" data-id="element-1980" data-mp="1z2oq2xks0d9d">{s.term}</span>
                            <span className="tabular-nums text-muted" data-id="element-1981" data-mp="1z518tcycyrnh">{s.count}×</span>
                          </li>)}
                      </ul>
                      <p className="mt-3 text-[12px] text-muted" data-id="element-1982" data-mp="1pbu0bj8bmvj6">
                        Een vraag die er al is. Een{' '}
                        <Link to={`${ADMIN_BASE}/journal`} className="text-ink underline underline-offset-2 hover:text-clay" data-id="element-1983" data-mp="1kbzdprvkws3x">
                          artikel
                        </Link>{' '}
                        of product hierover wordt vanzelf gevonden.
                      </p>
                    </>}
                </div>
              </div>
            </Panel>
          </>;
    }}
    </ReportView>;
}